<script setup>
  import { ref, onMounted, computed, watch, nextTick, onUnmounted, defineAsyncComponent } from "vue";
  import SwiperImages from "./SwiperImages.vue";
  import SwiperProjects from "./SwiperProjects.vue";
  import CanvasComponent from "./canvasComponent.vue";
  import CardPageProdComp from "./CardPageProdComp.vue";
  import CardProdCollec from "./CardProdCollec.vue";
  import FieldMaterials from "./FieldMaterials.vue";

  const StockBadge = defineAsyncComponent(() => import("./StockBadge.vue"));

  const props = defineProps({
    breadcrumb: {
      type: Array,
      default: () => []
    },
    model: Object,
    productBase: {
      type: Boolean,
      default: false
    },
    user: {
      type: Object,
      default: () => ({}),
    },
    productName: { type: String, default: "" },
  });
  const emits = defineEmits(["product-navigated"]);
  const currentModel = ref(props.model);
  const isFixed = ref(true);
  const buttonsPlaceholderRef = ref(null);
  const compositeProductSelected = ref(null);
  const fieldMaterials = ref([]);
  const products = ref([]);
  const article_list = ref(currentModel.value.article_list || []);

  const isOpen = ref(false);
  const trans = (key) => {
    return _.get(window.translations, key, key);
  };

  // Breadcrumb dinâmico baseado no URL atual e no modelo ativo
  const dynamicBreadcrumb = computed(() => {
    if (!props.breadcrumb || props.breadcrumb.length === 0) return [];
    const bread = [...props.breadcrumb];
    if (bread.length > 0) {
      bread[bread.length - 1] = {
        ...bread[bread.length - 1],
        label: currentModel.value.name,
        url: window.location.pathname + window.location.search
      };
    }
    return bread;
  });

  const getCanvasSize = (material) => {
    const sizeMap = {
      "25%": 25,
      "50%": 50,
      "65%": 65,
      "80%": 80
    };

    return sizeMap[material.svgSize] || 80;
  };

  const handleScroll = () => {
    if (buttonsPlaceholderRef.value) {
      const rect = buttonsPlaceholderRef.value.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      isFixed.value = rect.top > windowHeight - 80;
    }
  };

  const goBack = () => {
    const hasHistory = window.history.length > 1 && document.referrer !== "";
    if (hasHistory) {
      window.history.back();
    } else {
      if (dynamicBreadcrumb.value.length > 1) {
        const prevItem = dynamicBreadcrumb.value[dynamicBreadcrumb.value.length - 2];
        window.location.href = prevItem.url;
      } else {

        window.history.back();
      }
    }
  };

  const changeFieldMaterials = (compositeProduct) => {
    if (compositeProductSelected.value && compositeProductSelected.value.id === compositeProduct.id) {
      compositeProductSelected.value = null;
      fieldMaterials.value = [];
    } else {
      compositeProductSelected.value = compositeProduct;
      fieldMaterials.value = [];

      setTimeout(() => {
        fieldMaterials.value = compositeProduct.compositeProduct;
      }, 50);
    }
  };

  const navigateProduct = (newModel) => {
    currentModel.value = newModel;
    fieldMaterials.value = [];
    compositeProductSelected.value = null;

    article_list.value = currentModel.value.article_list;
    updateUrl(currentModel.value.slug);
    emits("product-navigated", newModel);
  };

  const nextProduct =  async() => {
    if (products.value.length === 0) return;
    const index = products.value.findIndex(product => product.id === currentModel.value.id);
    const newModel = products.value[index + 1] || products.value[0];
    try {
      const { data } = await axios.get(newModel.apiSlug, {});

      navigateProduct(data.model[0]);
    } catch (error) {
      console.error(error);
    }
  };

  const prevProduct = async () => {
    if (products.value.length === 0) return;
    const index = products.value.findIndex(product => product.id === currentModel.value.id);
    const newModel = products.value[index - 1] || products.value[products.value.length - 1];
    try {
      const { data } = await axios.get(newModel.apiSlug, {});

      navigateProduct(data.model[0]);
    } catch (error) {
      console.error(error);
    }
  };

  const updateUrl = (url) => {
    const pageUrl = new URL(window.location);
    const tempLink = new URL(url, window.location.origin);
    pageUrl.searchParams.forEach((value, key) => {
      tempLink.searchParams.append(key, value);
    });

    window.history.replaceState(null, "", tempLink.toString());
  };

  const fetchNextProducts = () => {
    try {
      const url = new URL(window.location.href);
      const lastSegment = url.pathname.split("/").filter(Boolean).pop();
      const requestData = {
        url: url.pathname.replace(lastSegment, "")
      };

      url.searchParams.forEach((value, key) => {
        requestData[key] = value;
      });
      const cacheName = "next-prev-products-cache-v1";
      const cacheKey = `next_prev_products_cache:${JSON.stringify(requestData)}`;
      async function setInCache(key, value) {
        const cache = await caches.open(cacheName);
        await cache.put(
          key,
          new Response(JSON.stringify(value), {
            headers: { "Content-Type": "application/json" }
          })
        );
      }
      async function getFromCache(key) {
        const cache = await caches.open(cacheName);
        const match = await cache.match(key);
        if (!match) return null;
        return await match.json();
      }

      const normalizeProductSlugs = (items) => {
        const pageUrl = new URL(window.location);
        const currentLastSegment = pageUrl.pathname.split("/").filter(Boolean).pop();
        return (items || []).map((product) => {
          const tempLink = new URL(product.slug, window.location.origin);
          const productLastSegment = tempLink.pathname.split("/").filter(Boolean).pop();
          const newLink = new URL(`${pageUrl.pathname.replace(currentLastSegment, "")}${productLastSegment}`, window.location.origin);
          return {
            ...product,
            slug: newLink.pathname,
          };
        });
      };

      nextTick(async ()=>{
        if ("caches" in window) {
          const cachedResponse = await getFromCache(cacheKey);
          if (cachedResponse) {
            products.value = normalizeProductSlugs(cachedResponse);
          }
        }
      })
      setTimeout(async ()=>{
        requestAnimationFrame(async () => {
          try {
            const csrfToken = document.head.querySelector("meta[name=\"csrf-token\"]").content;
            const { data } = await axios.post(`/integrations/topcer_products_next_prev_article`, requestData, {
              headers: {
                "X-CSRF-TOKEN": csrfToken
              }
            });

            if ("caches" in window) {
              await setInCache(cacheKey, data);
            }

            products.value = normalizeProductSlugs(data);
          } catch (error) {
          }
        });
      }, 50);



    } catch (error) {
    }
  };

  onMounted(() => {
    fetchNextProducts();
    window.addEventListener("scroll", handleScroll);
    nextTick(() => {
      setTimeout(handleScroll, 500);
    });
  });

  onUnmounted(() => {
    window.removeEventListener("scroll", handleScroll);
  });
</script>

<template>
  <div class="container-page-product-collections">
    <div class="container-product-collections">
      <div class="content">
        <div class="container-breadcrumb">
          <nav aria-label="breadcrumb">
            <ol class="breadcrumb">
              <li class="product">Products</li>
              <li
                v-for="(item, index) in dynamicBreadcrumb"
                :key="index"
                class="breadcrumb-item"
                :class="{ active: index === dynamicBreadcrumb.length - 1 }"
                :aria-current="index === dynamicBreadcrumb.length - 1 ? 'page' : null">
                <a v-if="index < dynamicBreadcrumb.length - 1" :href="item.url">{{ item.label }}</a>
                <span v-else>{{ item.label }}</span>
              </li>
            </ol>
          </nav>
        </div>
        <div class="button-content">
        <span @click="goBack" class="button-btn button-back">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 20" fill="none">
           <g clip-path="url(#clip0_27_1361)">
           <path d="M9.88919 0.115234L0.230469 9.99828L9.88919 19.8813" stroke="#333" stroke-miterlimit="10" />
           </g>
           <defs>
           <clipPath id="clip0_27_1361">
           <rect width="10" height="20" fill="white" />
           </clipPath>
           </defs>
          </svg>
          {{ trans("Back") }}
        </span>
        </div>
        <div class="content-header">
          <div class="container-text">
            <div>
              <h1>{{ currentModel.name }}</h1>
              <p>{{ trans("Ref") }}. {{ currentModel.reference }}</p>
            </div>
            <!--STOCK-->
            <div  class="stock-badge-container">
              <StockBadge
                :product-name="currentModel.name"
                :showStock="currentModel.showStock"
                :user="user"
                :stock_type="currentModel.stock_type"
                :stock="currentModel.stock"
                :made_to_order="currentModel.made_to_order"
              />
            </div>
          </div>

          <div class="container-related" v-if="currentModel.relatedProduct && currentModel.relatedProduct.length > 0">
            <button
              @click="isOpen = !isOpen"
              class="accordion-trigger"
              :aria-expanded="isOpen">
              <span>See Also</span>
              <svg class="icon"
                   :class="{ 'rotated': isOpen }"
                   xmlns="http://www.w3.org/2000/svg" width="7" height="11" viewBox="0 0 7 11" fill="none">
                <path d="M0.353577 10.3535L5.35358 5.35352L0.353576 0.353516" stroke="#333333" stroke-miterlimit="10" />
              </svg>
            </button>
            <transition name="fade">
              <ul v-if="isOpen" class="accordion-content">
                <li v-for="related in currentModel.relatedProduct" :key="related.id">
                  <a :href="related.slug">{{ related.name }}
                    <div class="card-image-link">
                      <img :src="related.featured_image.fullUrl" alt="">
                    </div>
                  </a>
                </li>
              </ul>
            </transition>
          </div>
        </div>

        <div class="container-geral">
          <div class="container-images">
            <div class="gallery-wrapper">
              <SwiperImages v-if="currentModel.dynamicGalleries && currentModel.dynamicGalleries.length > 0"
                            :images="currentModel.dynamicGalleries"
                            :show-description="true">
              </SwiperImages>
            </div>
          </div>
          <div class="container-info">
            <div class="content-composition"
                 v-if="currentModel.compositeProduct && currentModel.compositeProduct.length > 0">
              <h2>{{ trans("Composition") }}</h2>

              <div class="composition cursor">
                <div class="content-composition" v-for="composite in currentModel.compositeProduct" :key="composite.id">
                  <div class="content-trimpieces" v-if="currentModel.majorCategory === 'Trim Pieces'">
                    <div v-if="composite.compositeProduct.length > 0">
                      <div class="card card-prod-top"
                           :class="{ 'active': compositeProductSelected && compositeProductSelected.id === composite.id }"
                           @click="changeFieldMaterials(composite)">
                        <div class="composite-image" v-if="composite.packing_dimensions">
                          <div class="ballon-tip">
                            <img
                              :src="composite.featured_image.fullUrl"
                              alt="">
                          </div>

                        </div>
                        <div class="composite-image" v-else>
                          <img
                            :src="composite.featured_image.fullUrl"
                            alt="">
                        </div>
                        <div class="composite-info">
                          <p class="name">{{ composite.reference }} </p>
                          <StockBadge
                            :product-name="composite.name"
                            :showStock="composite.showStock"
                            :user="user"
                            :stock_type="composite.stock_type"
                            :stock="composite.stock"
                            :made_to_order="composite.made_to_order"
                            :dot="true"
                          />
                          <p class="reference">{{ composite.packing_dimensions }}</p>
                        </div>
                      </div>
                    </div>
                    <div v-else-if="composite.featured_image.isSvg === true">
                      <template v-for="color in composite.colors" :key="color.id">
                        <div class="card-prod" style="width: 166px">
                          <div class="card-composite" v-for="(image, index) in composite.dynamicGalleries" :key="index">
                            <div class="card" v-if="index === 0">
                              <div class="card-image">
                                <img :src="image.fullUrl" alt="">
                              </div>
                              <div class="card-body">
                                <p class="name-reference">{{ composite.reference }}</p>
                                <p>{{ composite.packing_dimensions }}</p>
                              </div>
                            </div>
                          </div>

                        </div>
                      </template>
                    </div>
                    <div v-else-if="composite.featured_image.isSvg === false">
                      <div class="card-composite">
                        <div class="card">
                          <div class="card-image">
                            <img :src="composite.featured_image.fullUrl" alt="">
                          </div>
                          <div class="card-body">
                            <p class="name-reference">{{ composite.reference }}</p>
                            <p>{{ composite.packing_dimensions }}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else>
                    <div v-if="composite.compositeProduct.length > 0">
                      <div class="card card-prod-top"
                           :class="{ 'active': compositeProductSelected && compositeProductSelected.id === composite.id }">
                        <CardProdCollec
                          :product="composite"
                          :user="user"
                          @select-material="changeFieldMaterials"
                        />
                      </div>
                    </div>
                    <div v-else-if="composite.featured_image.isSvg === true">
                      <template v-for="color in composite.colors" :key="color.id">
                        <CardPageProdComp
                          :user="user"
                          :product ="composite"
                        />
                      </template>
                    </div>
                    <div v-else-if="composite.featured_image.isSvg === false">
                      <div class="card-composite">
                        <div v-if="composite.compositeProduct > 0">
                          <div class="card">
                            <div class="card-image">
                              <img :src="composite.featured_image.fullUrl" alt="">
                            </div>
                            <div class="card-body">
                              <p class="name-reference">{{ composite.reference }}</p>
                              <p>{{ composite.packing_dimensions }}</p>
                            </div>
                          </div>
                        </div>
                        <div v-else class="no-composite">
                          <div class="card">
                            <div class="card-image">
                              <CardPageProdComp
                                :user="user"
                                :product ="composite"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <transition name="expand">
              <div class="content-composition" id="compition-prod" v-if="fieldMaterials && fieldMaterials.length > 0">
                <div class="composition" :key="compositeProductSelected ? compositeProductSelected.id : 'default'">
                  <div v-for="material in fieldMaterials" :key="material.id" class="card-content">
                    <CardPageProdComp
                      :user="user"
                      :product ="material"
                    ></CardPageProdComp>
                  </div>
                </div>
              </div>
            </transition>

            <div class="content-colours" v-if="currentModel.colors && currentModel.colors.length > 0">
              <div class="content-cards">
                <div class="colours">
                  <h3>{{ trans("Colours") }}</h3>
                  <div class="content-colors">
                    <div class="div" v-for="color in currentModel.colors">
                      <div class="card"
                           style="margin-right: 0"
                           :title="color.name">
                        <div class="card-image">
                          <img :src="color.featured_image_300x300" alt="color.name">
                        </div>
                        <p>{{ String(color.value).padStart(2, '0') }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="content-reference-product" v-if="currentModel.packing_dimensions">
              <h3>{{ trans("Sizes") }}</h3>
              <p>{{ currentModel.packing_dimensions }} mm</p>
            </div>
            <div class="content-resources" v-if="currentModel.documents && currentModel.documents.length > 0">
              <h5>{{ trans("Resources") }}</h5>
              <div v-for="document in currentModel.documents" class="container-file" :key="document.id">
                <div v-for="file in document.files" class="content-file" :key="file.fullUrl">
                  <a target="_blank" :href="file.fullUrl">{{ document.name }}</a>
                </div>
              </div>
            </div>
            <div class="content-description" v-if="currentModel.content && currentModel.content.length > 0">
              <h6>{{ trans("Description") }}</h6>
              <div class="description" v-html="currentModel.content">
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="content-projects" v-if="currentModel.article_list && currentModel.article_list.length > 0">
        <div class="projects">
          <h6 class="title-reference">{{ trans("Reference Projects") }}</h6>
          <swiper-projects :articles="currentModel.article_list"></swiper-projects>
        </div>
      </div>
      <div class="buttons-anchor" ref="buttonsPlaceholderRef">
        <div class="content-buttons"
             :class="{ 'is-fixed': isFixed }"
             v-if="currentModel.majorCategory === 'Contemporaneo series' ||
                   currentModel.majorCategory === 'Victorian Designs' ||
                   currentModel.majorCategory === 'Victorian Borders and corners'">
            <span @click="prevProduct" class="button-btn button-prev">
                {{ trans("Previous Design") }}
            </span>
          <span @click="nextProduct" class="button-btn button-next">
                {{ trans("Next Design") }}
            </span>
        </div>
        <div class="content-buttons"
             :class="{ 'is-fixed': isFixed }"
             v-else>
             <span @click="prevProduct" class="button-btn button-prev">
                {{ trans("Previous") }}
            </span>
          <span @click="nextProduct" class="button-btn button-next">
                {{ trans("Next") }}
            </span>
        </div>
      </div>

    </div>
  </div>
</template>

<style scoped>
  .mySwiper {
    width: 100%;
    height: 350px;
  }


  /* Transição suave para o accordion */
  .fade-enter-active, .fade-leave-active {
    transition: all 0.3s ease;
    overflow: hidden;
  }

  .fade-enter-from, .fade-leave-to {
    max-height: 0;
    opacity: 0;
    padding-top: 0;
    padding-bottom: 0;
  }

  .fade-enter-to, .fade-leave-from {
    max-height: 500px;
    opacity: 1;
  }

  .buttons-anchor {
    width: 100%;
    min-height: 80px;
    display: flex;
    justify-content: center;
    margin-top: 40px;
  }

  .content-buttons {
    display: flex;
    padding: 20px 16px;
    align-items: center;
    justify-content: center;
    gap: 30px;
    width: 100%;
    transition: background 0.3s ease;
  }

  .content-buttons.is-fixed {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    background-color: rgba(255, 255, 255, 0.95);
    box-shadow: 0 -2px 15px rgba(0, 0, 0, 0.1);
    padding: 15px 16px;
    animation: slideUp 0.3s ease-out;
  }

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }

  .expand-enter-from, .expand-leave-to {
    max-height: 0;
    opacity: 0;
  }

  .expand-enter-active, .expand-leave-active {
    transition: all 0.5s ease;
    overflow: hidden;
  }

  .expand-enter-to, .expand-leave-from {
    max-height: 1000px;
    opacity: 1;
  }

  .accordion-trigger .icon {
    transition: transform 0.3s ease;
  }

  .accordion-trigger .icon.rotated {
    transform: rotate(90deg);
  }
</style>
